import { useEffect, useMemo, useRef, useState } from "react";
import Card from "components/card";
import PieChart from "components/charts/PieChart";
import api from "utils/api";
import { useAuth } from "context/AuthContext";
import { buildDynamicPieOptions } from "utils/chartOptions";

/** Helpers */
const fmtMonth = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};
const isValidMonth = (s) => /^\d{4}-\d{2}$/.test(s);

const STATIC_COLORS = ["#4318FF", "#6AD2FF", "#EFF4FB", "#22C55E", "#F59E0B", "#EF4444"];

const PieChartCard = () => {
  const { role } = useAuth();
  const [month, setMonth] = useState(fmtMonth(new Date()));
  const [labels, setLabels] = useState([]);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const abortRef = useRef(null);

  const normalize = (arr) =>
    (Array.isArray(arr) ? arr : []).map((d) => ({
      name: d?.service_name ?? "Unknown",
      count: Number(d?.count ?? 0),
    }));

  const fetchData = async (monthStr) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    if (role !== "SA" && role !== "A") {
      setLabels([]);
      setSeries([]);
      setErr("Access restricted. Admin only.");
      setLoading(false);
      return;
    }

    if (!isValidMonth(monthStr)) {
      setErr("Pick a valid month (YYYY-MM).");
      setLabels([]);
      setSeries([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setErr("");

    try {
      const res = await api.get("/admin/booking_per_service", {
        params: { month: monthStr, include_cancelled: false },
        signal: controller.signal,
      });

      const norm = normalize(res?.data);
      norm.sort((a, b) => b.count - a.count);

      setLabels(norm.map((x) => x.name));
      setSeries(norm.map((x) => x.count));
      setErr("");
    } catch (e) {
      if (e.name === "CanceledError" || e.name === "AbortError") return;
      console.error("Failed to load pie data:", e);
      setErr("Failed to load data");
      setLabels([]);
      setSeries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(month);
  }, [role, month]);

  const total = useMemo(() => series.reduce((a, b) => a + b, 0), [series]);
  const options = useMemo(() => buildDynamicPieOptions(labels, STATIC_COLORS), [labels]);

  return (
    <Card extra="rounded-[20px] p-3 flex flex-col h-[420px]">
      {/* Header */}
      <div className="flex flex-row justify-between px-3 pt-2">
        <div>
          <h4 className="text-lg font-bold text-navy-700 dark:text-white">
            Bookings by Service
          </h4>
          <p className="text-xs text-gray-500 dark:text-gray-400" aria-live="polite">
            {loading ? "Loading..." : err ? "Error" : total > 0 ? `${total} total bookings` : "No data"}
          </p>
        </div>

        <input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="text-sm font-bold text-gray-600 dark:text-white"
          aria-label="Select month"
        />
      </div>

      {/* Chart */}
      <div className="flex flex-1 w-full items-center justify-center mt-2">
        {err ? (
          <span className="text-sm text-red-500">{err}</span>
        ) : loading ? (
          <div className="animate-pulse text-xs text-gray-400">Loading chart…</div>
        ) : total === 0 ? (
          <div className="text-xs text-gray-500">No bookings for this month.</div>
        ) : (
          // Make chart fill available height inside the 420px card
          <div className="w-full h-full flex items-center justify-center">
            <PieChart options={options} series={series} redrawOnResize={true} />
          </div>
        )}
      </div>

      {/* Bottom stat blocks */}
      <div className="flex flex-row !justify-between mt-4 rounded-2xl px-6 py-3 shadow-2xl shadow-shadow-500 dark:!bg-navy-700 dark:shadow-none">
        {/* Left block */}
        <div className="flex flex-col items-center justify-center">
          <div className="flex items-center justify-center">
            <div className="h-2 w-2 rounded-full" style={{ background: options.colors?.[0] ?? "#4318FF" }} />
            <p className="ml-1 text-sm font-normal text-gray-600 dark:text-gray-300">
              {labels[0] ?? "—"}
            </p>
          </div>
          <p className="mt-px text-xl font-bold text-navy-700 dark:text-white">
            {total ? Math.round(((series?.[0] ?? 0) / total) * 100) : 0}%
          </p>
        </div>

        <div className="h-11 w-px bg-gray-300 dark:bg-white/10" />

        {/* Right block */}
        <div className="flex flex-col items-center justify-center">
          <div className="flex items-center justify-center">
            <div className="h-2 w-2 rounded-full" style={{ background: options.colors?.[1] ?? "#6AD2FF" }} />
            <p className="ml-1 text-sm font-normal text-gray-600 dark:text-gray-300">
              {labels[1] ?? "—"}
            </p>
          </div>
          <p className="mt-px text-xl font-bold text-navy-700 dark:text-white">
            {total ? Math.round(((series?.[1] ?? 0) / total) * 100) : 0}%
          </p>
        </div>
      </div>
    </Card>
  );
};

export default PieChartCard;